import { c as _c } from "react/compiler-runtime";
import { GridModeChips, SubquestionsSection, useGridMode, useSubqFilters } from './Subqs';
import { ActionIcon, Alert, Button, Portal as MantinePortal, Switch, Tabs, Tooltip } from '@mantine/core';
import { isQuestionOwner, useQuestionForm, useQuestionQuery } from './useQuestions';
import { match, P } from 'ts-pattern';
import { useQueryString } from 'utils/useQueryString';
import { pick } from 'remeda';
import { useEffect, useMemo, useRef, useState } from 'react';
import { SErrorBoundary, LabelForm, useHover } from 'shared/ui';
import { usePortal } from 'components/portal/portal';
import { BottomTab, QuestionContext, QuestionContextProps, useQuestionContext, zQuestionQuery } from './questionComponentTypes';
import { HiArrowSmLeft, HiChevronDoubleRight, HiClipboardCheck, HiSortDescending } from 'react-icons/hi';
import { QuestionFilterItems } from './Questions';
import { useCommentExpand, zCommentQuery } from 'components/discussion/comments';
import { QuestionEditForecast } from './QuestionEditForecast';
import { DateTime } from 'luxon';
import { useLatestActivity } from 'components/portal/activity';
import { Panel, PanelGroup, type ImperativePanelHandle, PanelResizeHandle } from 'react-resizable-panels';
import { doSafe, doTry } from 'shared/promise';
import { CommentPanel, CommentsStats, useCommentStats } from 'components/discussion/Comments';
import { ScreenModeButton, UseScreenMode, useScreenMode } from 'components/portal/ScreenMode';
import { TopBarMenuPortalSelector } from 'components/portal/TopBar';
import { tourTipProps } from 'components/portal/Tour';
import { QuestionForm } from './QuestionForm';
import { Sticky } from 'components/Sticky';
import { showForecastsStates, type ShowForecastState } from 'shared/forecast/subqs';
import { AppTitle } from 'components/portal/Title';
import { questionStatusMd } from 'shared/forecast/questionStatus';
import { O } from 'ts-toolbelt';
import { ForecastTable } from './ForecastsTable';
import { QuestionParticipationSection } from './Participation';
import { QuestionResultsSection } from './QuestionResultsSection';
import { ChipRadioGroup } from 'components/form/ChipRadioGroup';
const zQuery = zQuestionQuery.merge(zCommentQuery.pick({
  comment_id: true
}));
export const Question = ({
  question_id
}: {
  question_id: string;
}) => {
  const commentPanelRef = useRef<ImperativePanelHandle>(null);
  const {
    commentExpand,
    setCommentExpand
  } = useCommentExpand();
  useEffect(() => {
    const panel = commentPanelRef.current;
    if (!panel) return;
    const actualExpand = (doTry(() => panel.getSize(), _ => undefined) ?? 0) > 0;
    if (commentExpand == actualExpand) return;
    match(actualExpand).with(true, () => {
      panel.collapse();
    }).with(false, () => {
      doTry(() => panel.resize(30));
    });
  }, [commentPanelRef.current, commentExpand]);
  const question = useQuestionQuery({
    question_id: question_id,
    pick: ({
      id,
      cfg,
      created_by
    }) => ({
      id,
      created_by,
      cfg: pick(cfg, ['owner', 'disableProposed', 'disableQuestionForecast', 'status', 'title'])
    })
  }).data;
  const {
    profile,
    workspace_id,
    readonly: portalReadonly
  } = usePortal();
  const commentModeCls = 'pl-1';
  const screenMode = useScreenMode();
  const [landedAt] = useState(DateTime.utc());
  const {
    data: previousActivity
  } = useLatestActivity({
    workspace_id,
    question_id,
    type: 'view',
    before: landedAt.minus({
      seconds: 1
    }).toISO()
  });
  if (!profile) return <></>;
  if (!profile.hasPermission('question-view')) return <></>;
  const ctx = useMemo(() => {
    if (!question || !question.ok) return undefined;
    const readonly = !profile.hasPermission('question-edit') || portalReadonly;
    return {
      question_id,
      type: 'question',
      isOwner: isQuestionOwner(question, profile),
      latestView: doSafe(previousActivity?.created_at, c => DateTime.fromISO(c)),
      readonly
    } satisfies QuestionContextProps;
  }, [question_id, profile, portalReadonly, previousActivity?.created_at, question]);
  return match(question).with({
    ok: false
  }, ({
    message
  }) => <div>
        <Alert color='red' className='mt-6 max-w-[40em] mx-auto' title='Error loading question'>
          {message}
        </Alert>
      </div>).with({
    ok: true
  }, question_0 => {
    if (!ctx) return <></>;
    const questionInnerProps = {
      question_id,
      readonly: ctx?.readonly
    };
    return <QuestionContext.Provider value={ctx}>
          <AppTitle title={question_0.cfg.title} />
          <MantinePortal target={`${TopBarMenuPortalSelector}`}>
            <ScreenModeOrCommentButton setCommentExpand={setCommentExpand} commentExpand={commentExpand} screenMode={screenMode} />
          </MantinePortal>
          {match(screenMode.modal).with(P.nullish, () => <></>).with(true, () => <>
                <div
        // ScrollArea is nicer, but it scrolls x instead of making the children shrink/wrap/scroll
        className={`${commentModeCls} ml-1 overflow-x-clip overflow-y-auto`} style={{
          display: screenMode.mode == 'form' ? undefined : 'none'
        }}>
                  <SErrorBoundary>
                    <QuestionInner {...questionInnerProps} />
                  </SErrorBoundary>
                </div>
                <div className={commentModeCls} style={{
          display: screenMode.mode == 'comments' ? undefined : 'none'
        }}>
                  <SErrorBoundary>
                    <CommentPanel />
                  </SErrorBoundary>
                </div>
              </>).with(false, () => <PanelGroup direction='horizontal'>
                <Panel id='form' minSize={40} className=''>
                  <div className='h-full md:px-3 px-2 overflow-y-auto'>
                    <SErrorBoundary>
                      <QuestionInner {...questionInnerProps} />
                    </SErrorBoundary>
                  </div>
                </Panel>
                <ResizeHandle />
                <Panel id='comments' defaultSize={commentExpand ? 35 : 0} minSize={10} collapsedSize={0} onResize={size => {
          const actualExpand_0 = size > 0;
          if (commentExpand != actualExpand_0) setCommentExpand(actualExpand_0);
        }} collapsible ref={commentPanelRef} className='h-full'>
                  <SErrorBoundary>
                    <CommentPanel menuLeft={<ActionIcon onClick={() => {
              setCommentExpand(false);
            }}>
                          <HiChevronDoubleRight />
                        </ActionIcon>} />
                  </SErrorBoundary>
                </Panel>
              </PanelGroup>).exhaustive()}
        </QuestionContext.Provider>;
  }).otherwise(() => <></>);
};
const ScreenModeOrCommentButton = t0 => {
  const $ = _c(12);
  const {
    setCommentExpand,
    commentExpand,
    screenMode
  } = t0;
  const {
    question_id
  } = useQuestionContext();
  const {
    query
  } = useQueryString(zQuery);
  let t1;
  if ($[0] !== query?.comment_id || $[1] !== screenMode) {
    t1 = () => {
      if (!query?.comment_id) {
        return;
      }
      if (screenMode.mode != "comments" && screenMode.modal) {
        screenMode.setMode("comments");
      }
    };
    $[0] = query?.comment_id;
    $[1] = screenMode;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = query?.comment_id;
  let t3;
  if ($[3] !== t2) {
    t3 = [t2];
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  useEffect(t1, t3);
  let t4;
  if ($[5] !== question_id) {
    t4 = {
      question_id
    };
    $[5] = question_id;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const commentStats = useCommentStats(t4);
  let t5;
  if ($[7] !== commentExpand || $[8] !== commentStats || $[9] !== screenMode || $[10] !== setCommentExpand) {
    t5 = screenMode.modal ? <ScreenModeButton {...screenMode} {...commentStats} /> : <ActionIcon {...tourTipProps("comment-button-collapsed", !commentExpand && (commentStats.total ?? 0) > 0)} className="mr-1" styles={{
      root: {
        overflow: "visible"
      }
    }} onClick={() => setCommentExpand(!commentExpand)}><CommentsStats {...commentStats} /></ActionIcon>;
    $[7] = commentExpand;
    $[8] = commentStats;
    $[9] = screenMode;
    $[10] = setCommentExpand;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
};
const QuestionInner = t0 => {
  const $ = _c(87);
  const {
    question_id,
    readonly
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = zQuery.pick({
      tab: true
    });
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    query,
    setQuery
  } = useQueryString(t1);
  const {
    profile,
    workspace: t2,
    readonly: readonlyPortal
  } = usePortal();
  const {
    advancedUI
  } = t2;
  const subqFilters = useSubqFilters();
  const displayMode = subqFilters.query.subqDisplayMode;
  let t3;
  if ($[1] !== subqFilters) {
    t3 = mode => subqFilters.setQuery({
      subqDisplayMode: mode
    });
    $[1] = subqFilters;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const setDisplayMode = t3;
  const uGridMode = useGridMode();
  let t4;
  if ($[3] !== question_id) {
    t4 = {
      question_id,
      pick: _temp
    };
    $[3] = question_id;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const qForm = useQuestionForm(t4).form;
  if (!qForm.values) {
    let t5;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <></>;
      $[5] = t5;
    } else {
      t5 = $[5];
    }
    return t5;
  }
  const {
    disableProposed: disableProposed_0,
    disableQuestionForecast: disableQuestionForecast_0,
    status: status_0
  } = qForm.values;
  const ended = questionStatusMd[status_0].ended;
  const bottomTab = query?.tab ?? (ended || readonlyPortal ? "results" : "cfg");
  let t5;
  if ($[6] !== advancedUI || $[7] !== profile) {
    t5 = advancedUI && (profile.hasRole("admin") || profile.hasPermission("forecast"));
    $[6] = advancedUI;
    $[7] = profile;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const showParticipationTab = t5;
  let t6;
  if ($[9] !== subqFilters.setShowForecasts || $[10] !== subqFilters.showForecasts) {
    t6 = {
      value: subqFilters.showForecasts,
      onChange: subqFilters.setShowForecasts
    };
    $[9] = subqFilters.setShowForecasts;
    $[10] = subqFilters.showForecasts;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const showFilterProps = t6;
  let t7;
  if ($[12] !== question_id) {
    t7 = <QuestionForm question_id={question_id} />;
    $[12] = question_id;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      tabLabel: "font-semibold  text-[1.2em] group-data-[sticking=true]:text-[1.1em]",
      tab: "md:!px-3 !px-2 opacity-60 data-[active=true]:!opacity-100 data-[active=true]:text-[var(--mantine-color-primary-7)]"
    };
    t9 = {
      tab: {
        borderWidth: "0px 0px 2px 0px"
      }
    };
    $[14] = t8;
    $[15] = t9;
  } else {
    t8 = $[14];
    t9 = $[15];
  }
  let t10;
  if ($[16] !== setQuery) {
    t10 = v => {
      setQuery({
        tab: v as BottomTab
      });
    };
    $[16] = setQuery;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  let t12;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Tabs.Tab value="cfg">Main</Tabs.Tab>;
    t12 = <Tabs.Tab value="results">Results</Tabs.Tab>;
    $[18] = t11;
    $[19] = t12;
  } else {
    t11 = $[18];
    t12 = $[19];
  }
  let t13;
  if ($[20] !== showParticipationTab) {
    t13 = showParticipationTab && <Tabs.Tab value="participation">Participation</Tabs.Tab>;
    $[20] = showParticipationTab;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== readonly) {
    t14 = !readonly && <Tabs.Tab value="forecasts">Forecasts Table</Tabs.Tab>;
    $[22] = readonly;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== bottomTab || $[25] !== showFilterProps) {
    t15 = bottomTab == "cfg" && <ShowFilter {...showFilterProps} />;
    $[24] = bottomTab;
    $[25] = showFilterProps;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== t15) {
    t16 = <div className="hidden group-data-[sticking=true]:flex self-center ml-4 font-semibold gap-4 min-[600px]:min-w-0">{t15}</div>;
    $[27] = t15;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== t13 || $[30] !== t14 || $[31] !== t16) {
    t17 = <Sticky className="-mx-3 max-w-full"><Tabs.List className="bg-[var(--mantine-color-body)] group-data-[sticking=true]:shadow-sm !flex-nowrap px-2 flow-x-auto max-[600px]:scrollbar-none">{t11}{t12}{t13}{t14}{t16}</Tabs.List></Sticky>;
    $[29] = t13;
    $[30] = t14;
    $[31] = t16;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== disableQuestionForecast_0 || $[34] !== displayMode || $[35] !== qForm || $[36] !== readonly || $[37] !== setDisplayMode) {
    t18 = !readonly && qForm.values && <><Tooltip label="If switched off, forecasting is disabled/hidden for the main question"><div><Switch checked={!disableQuestionForecast_0} onChange={c => qForm.setFieldValue("disableQuestionForecast", !c.target.checked)} label="Main question" /></div></Tooltip><Tooltip label="If switched off, the ability to propose new sub-questions is disabled"><div><Switch checked={!qForm.values.disableProposed} onChange={c_0 => qForm.setFieldValue("disableProposed", !c_0.target.checked)} label="Proposals" className="mx-2" /></div></Tooltip><div>{displayMode != "forecast" && <Button variant="filled" leftSection={<HiArrowSmLeft />} onClick={() => setDisplayMode("forecast")}>Back to forecasts</Button>}{displayMode == "forecast" && <><Button leftSection={<HiSortDescending />} onClick={() => {
            setDisplayMode("order");
          }}>Order</Button><Button leftSection={<HiClipboardCheck />} onClick={() => {
            setDisplayMode("resolution");
          }}>Resolution values</Button></>}</div></>;
    $[33] = disableQuestionForecast_0;
    $[34] = displayMode;
    $[35] = qForm;
    $[36] = readonly;
    $[37] = setDisplayMode;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== disableProposed_0 || $[40] !== displayMode || $[41] !== readonly || $[42] !== showFilterProps || $[43] !== subqFilters) {
    t19 = displayMode == "forecast" && !subqFilters.showForecastsDisabled && <><ShowFilter {...showFilterProps} />{!readonly && !disableProposed_0 && <QuestionFilterItems className="mx-2" filter={{
        showDraft: subqFilters.query.showDraft ?? false
      }} setFilter={t20 => {
        const {
          showDraft
        } = t20;
        return subqFilters.setQuery({
          showDraft
        });
      }} />}</>;
    $[39] = disableProposed_0;
    $[40] = displayMode;
    $[41] = readonly;
    $[42] = showFilterProps;
    $[43] = subqFilters;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== uGridMode) {
    t20 = <GridModeChips {...uGridMode} />;
    $[45] = uGridMode;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== t18 || $[48] !== t19 || $[49] !== t20) {
    t21 = <div className="flex space-x-2 items-center justify-center flex-wrap mb-4 mt-2">{t18}{t19}{t20}</div>;
    $[47] = t18;
    $[48] = t19;
    $[49] = t20;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== disableProposed_0 || $[52] !== disableQuestionForecast_0 || $[53] !== uGridMode.gridMode) {
    t22 = {
      disableProposed: disableProposed_0,
      disableQuestionForecast: disableQuestionForecast_0,
      gridMode: uGridMode.gridMode
    };
    $[51] = disableProposed_0;
    $[52] = disableQuestionForecast_0;
    $[53] = uGridMode.gridMode;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  let t23;
  if ($[55] !== displayMode || $[56] !== profile || $[57] !== qForm.values) {
    t23 = profile?.hasPermission("forecast") && <SErrorBoundary><QuestionEditForecast displayMode={displayMode} className="mt-2" options={qForm.values} /></SErrorBoundary>;
    $[55] = displayMode;
    $[56] = profile;
    $[57] = qForm.values;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  let t24;
  if ($[59] !== t22 || $[60] !== t23) {
    t24 = <SubquestionsSection {...t22}>{t23}</SubquestionsSection>;
    $[59] = t22;
    $[60] = t23;
    $[61] = t24;
  } else {
    t24 = $[61];
  }
  let t25;
  if ($[62] !== t21 || $[63] !== t24) {
    t25 = <Tabs.Panel value="cfg">{t21}{t24}</Tabs.Panel>;
    $[62] = t21;
    $[63] = t24;
    $[64] = t25;
  } else {
    t25 = $[64];
  }
  let t26;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <Tabs.Panel value="results" className=""><SErrorBoundary><QuestionResultsSection /></SErrorBoundary></Tabs.Panel>;
    $[65] = t26;
  } else {
    t26 = $[65];
  }
  let t27;
  if ($[66] !== showParticipationTab) {
    t27 = showParticipationTab && <SErrorBoundary><QuestionParticipationSection /></SErrorBoundary>;
    $[66] = showParticipationTab;
    $[67] = t27;
  } else {
    t27 = $[67];
  }
  let t28;
  if ($[68] !== t27) {
    t28 = <Tabs.Panel value="participation" className="pt-4">{t27}</Tabs.Panel>;
    $[68] = t27;
    $[69] = t28;
  } else {
    t28 = $[69];
  }
  let t29;
  if ($[70] !== question_id || $[71] !== readonly) {
    t29 = !readonly && <SErrorBoundary><ForecastTable question_id={question_id} /></SErrorBoundary>;
    $[70] = question_id;
    $[71] = readonly;
    $[72] = t29;
  } else {
    t29 = $[72];
  }
  let t30;
  if ($[73] !== t29) {
    t30 = <Tabs.Panel value="forecasts" className="pt-4">{t29}</Tabs.Panel>;
    $[73] = t29;
    $[74] = t30;
  } else {
    t30 = $[74];
  }
  let t31;
  if ($[75] !== t17 || $[76] !== t25 || $[77] !== t28 || $[78] !== t30) {
    t31 = <>{t17}{t25}{t26}{t28}{t30}</>;
    $[75] = t17;
    $[76] = t25;
    $[77] = t28;
    $[78] = t30;
    $[79] = t31;
  } else {
    t31 = $[79];
  }
  let t32;
  if ($[80] !== bottomTab || $[81] !== t10 || $[82] !== t31) {
    t32 = <Tabs classNames={t8} styles={t9} value={bottomTab} onChange={t10} keepMounted={false}>{t31}</Tabs>;
    $[80] = bottomTab;
    $[81] = t10;
    $[82] = t31;
    $[83] = t32;
  } else {
    t32 = $[83];
  }
  let t33;
  if ($[84] !== t32 || $[85] !== t7) {
    t33 = <div className="flex flex-col gap-2 pt-1">{t7}{t32}</div>;
    $[84] = t32;
    $[85] = t7;
    $[86] = t33;
  } else {
    t33 = $[86];
  }
  return t33;
};
const ResizeHandle = () => {
  const $ = _c(6);
  const {
    ref,
    hovered
  } = useHover();
  const t0 = `absolute z-10 inset-y-0 left-1/2 h-full w-[4px] -translate-x-1/2 rounded-full ${hovered ? "bg-primary" : ""} transition-colors`;
  const t1 = `h-full w-px mx-auto ${hovered ? "" : "bg-gray-200"} transition-colors`;
  let t2;
  if ($[0] !== t1) {
    t2 = <div className={t1} />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== ref || $[3] !== t0 || $[4] !== t2) {
    t3 = <PanelResizeHandle className="relative"><div ref={ref} className={t0}>{t2}</div></PanelResizeHandle>;
    $[2] = ref;
    $[3] = t0;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};
const ShowFilter = t0 => {
  const $ = _c(6);
  const {
    value,
    onChange
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <LabelForm className="mr-1 text-sm">expand</LabelForm>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = onChange as any;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = showForecastsStates.map(_temp5);
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      classNames: {
        label: "!px-3 font-semibold !text-sm"
      }
    };
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== t2 || $[4] !== value) {
    t5 = <div className="flex items-center gap-[0.1em]">{t1}<ChipRadioGroup value={value} onChange={t2} items={t3} renderLabel={_temp6} chipProps={t4} /></div>;
    $[3] = t2;
    $[4] = value;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  return t5;
};
function _temp(t0) {
  const {
    disableProposed,
    disableQuestionForecast,
    status
  } = t0;
  return {
    disableProposed,
    disableQuestionForecast,
    status
  };
}
function _temp2() {
  return "collapse all forecast sections";
}
function _temp3() {
  return "expand all forecast sections you have not completed";
}
function _temp4() {
  return "expand all forecast sections";
}
function _temp5(v) {
  return {
    label: v,
    value: v,
    tip: match(v).with("none", _temp2).with("incomplete", _temp3).with("all", _temp4).exhaustive()
  };
}
function _temp6(item) {
  return item.value === "incomplete" ? "todo" : item.value;
}